import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { Input } from "../input/Input";
import { CancelXIcon } from "../../icons/CancelXIcon";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useUniqueId } from "../../hooks/useUniqueId";
import * as Styled from "./SearchField.styled";
import { clearInputValueAndEmitChangeEvent } from "./setInputValueNative";
import { stringMapDefault } from "./SearchField.stringMap";
const componentName = "SearchField";
export const SearchField = forwardRef((props, ref) => {
    const { inputProps, // TODO: clarify which props can be used, might make sense to not allow value/defaultValue/onChange
    inputRef: inputRefProp, isFullWidth, onChange, iconProps, clearButtonProps, value, defaultValue, stringMap: stringMapProp, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const inputRef = useRef(null);
    const combinedInputRef = useCombineRefs(inputRef, inputRefProp);
    const isControlled = value !== undefined;
    const [isClearButtonHiddenInternal, setIsClearButtonHiddenInternal] = useState();
    if (isClearButtonHiddenInternal === undefined && !isControlled) {
        setIsClearButtonHiddenInternal((defaultValue ?? "").trim().length === 0);
    }
    const handleChange = useCallback((e) => {
        (inputProps?.onChange ?? onChange)?.(e);
        if (!isControlled) {
            setIsClearButtonHiddenInternal(e.target.value.trim().length === 0);
        }
    }, [inputProps?.onChange, isControlled, onChange]);
    const isClearButtonHidden = inputProps?.disabled || (isControlled ? value.length === 0 : isClearButtonHiddenInternal ?? false);
    const { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy } = inputProps ?? {};
    // ↓ should be extracted to a hook with better API later
    const internalClearLabelId = useUniqueId("aui-search-field-clear-");
    const [clearLabelIds, setClearLabelIds] = useState();
    const [shouldRenderParentAriaLabel, setShouldRenderParentAriaLabel] = useState(false);
    useEffect(() => {
        if (ariaLabelledBy) {
            setShouldRenderParentAriaLabel(false);
            setClearLabelIds(`${internalClearLabelId} ${ariaLabelledBy}`);
            return;
        }
        if (ariaLabel) {
            setShouldRenderParentAriaLabel(true);
            setClearLabelIds(internalClearLabelId);
            return;
        }
        const labels = [];
        inputRef.current?.labels?.forEach((label) => {
            // TODO: generate ids internally if not in standard Field?
            if (label.id) {
                labels.push(label.id);
            }
        });
        if (labels.length) {
            setShouldRenderParentAriaLabel(false);
            setClearLabelIds(`${internalClearLabelId} ${labels.join(" ")}`);
            return;
        }
        // default (labeled by tooltip)
        setShouldRenderParentAriaLabel(false);
        setClearLabelIds("");
    }, [ariaLabel, ariaLabelledBy, internalClearLabelId]);
    return (_jsx(Styled.SearchField, { ref: ref, label: stringMap.searchLabelText, shouldVisuallyHideLabel: true, startAdornment: _jsx(Styled.SearchFieldIcon, { "aria-hidden": "true", size: 14, ...iconProps }), isFullWidth: isFullWidth, "data-testid": "SearchField", input: _jsx(Input, { ref: combinedInputRef, type: "search", isFullWidth: isFullWidth, value: value, defaultValue: defaultValue, ...inputProps, onChange: handleChange }), endAdornment: _jsxs(_Fragment, { children: [_jsxs("span", { id: internalClearLabelId, hidden: true, children: [stringMap.clearTooltipText, " ", shouldRenderParentAriaLabel && ariaLabel] }), _jsx(Styled.ClearButton, { disabled: isClearButtonHidden, tabIndex: isClearButtonHidden ? -1 : undefined, icon: _jsx(CancelXIcon, { "aria-hidden": "true" }), tooltipContents: stringMap.clearTooltipText, onMouseDown: (e) => {
                        e.preventDefault(); // prevent focus removal from input
                    }, onClick: () => {
                        if (!inputRef.current) {
                            return;
                        }
                        if (inputRef.current !== document.activeElement) {
                            // To prevent focus staying on
                            inputRef.current?.focus();
                        }
                        clearInputValueAndEmitChangeEvent(inputRef.current);
                    }, "aria-labelledby": clearLabelIds, ...clearButtonProps })] }), isClearButtonHidden: isClearButtonHidden, ...otherProps }));
});
SearchField.displayName = componentName;
SearchField.defaultProps = {
    isFullWidth: true
};
